import { jsPDF } from "jspdf";

const HEX_BG = "#0B3D91";
const HEX_ACCENT = "#00E5FF";

const drawHeader = (doc, title) => {
  doc.setFillColor(11, 61, 145);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 24, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("SilentScan AI", 14, 14);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(title, 14, 20);
  doc.setTextColor(30, 30, 30);
};

const line = (doc, y) => {
  doc.setDrawColor(210, 210, 210);
  doc.line(14, y, doc.internal.pageSize.getWidth() - 14, y);
};

const sectionTitle = (doc, text, y) => {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(11, 61, 145);
  doc.text(text.toUpperCase(), 14, y);
  doc.setTextColor(30, 30, 30);
  return y + 6;
};

const bodyText = (doc, text, y, opts = {}) => {
  doc.setFont("helvetica", opts.bold ? "bold" : "normal");
  doc.setFontSize(opts.size || 10);
  if (opts.color) {
    doc.setTextColor(...opts.color);
  } else {
    doc.setTextColor(30, 30, 30);
  }
  const wrapped = doc.splitTextToSize(
    text || "",
    doc.internal.pageSize.getWidth() - 28
  );
  doc.text(wrapped, 14, y);
  return y + wrapped.length * 5 + 2;
};

const ensureSpace = (doc, y, needed = 20) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (y + needed > pageHeight - 20) {
    doc.addPage();
    drawHeader(doc, "SilentScan AI — Personal Health Report (continued)");
    return 32;
  }
  return y;
};

export const downloadHealthReportPDF = (assessment) => {
  if (!assessment) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  drawHeader(doc, `Personal Health Report — ${today}`);
  let y = 32;

  // Big score
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(11, 61, 145);
  doc.text(`${assessment.score}/100`, 14, y);
  doc.setFontSize(12);
  doc.setTextColor(80, 80, 80);
  doc.text(assessment.label || "", 45, y);
  y += 8;
  doc.setDrawColor(0, 229, 255);
  doc.setLineWidth(0.8);
  doc.line(14, y, 80, y);
  y += 6;

  doc.setLineWidth(0.1);

  y = bodyText(doc, assessment.summary || "", y);

  if (assessment.confidence) {
    y = bodyText(
      doc,
      `Confidence: ${assessment.confidence} — ${
        assessment.confidence_reason || ""
      }`,
      y,
      { color: [90, 90, 90] }
    );
  }
  if (assessment.bmi != null) {
    y = bodyText(
      doc,
      `BMI: ${assessment.bmi}  (${assessment.bmi_category || "—"})`,
      y,
      { bold: true }
    );
  }
  y += 2;
  line(doc, y);
  y += 6;

  // Sub-scores
  const sub = assessment.sub_scores || {};
  y = sectionTitle(doc, "Wellness Dashboard", y);
  const subs = [
    ["Lifestyle", sub.lifestyle],
    ["Sleep", sub.sleep],
    ["Activity", sub.activity],
    ["Hydration", sub.hydration],
    ["Nutrition", sub.nutrition],
  ];
  subs.forEach(([k, v]) => {
    y = bodyText(doc, `${k}: ${v ?? "—"}/100`, y);
  });
  y += 3;

  // AI Health Twin — 6 dimensions
  const twin = assessment.twin_scores || {};
  const twinInsights = assessment.twin_insights || [];
  const twinLookup = {};
  twinInsights.forEach((t) => {
    if (t?.dimension) twinLookup[t.dimension] = t;
  });
  if (Object.keys(twin).length) {
    y = ensureSpace(doc, y, 20);
    y = sectionTitle(doc, "AI Health Twin", y);
    const dims = [
      ["Mind", twin.mind],
      ["Body", twin.body],
      ["Nutrition", twin.nutrition],
      ["Sleep", twin.sleep],
      ["Stress", twin.stress],
      ["Hydration", twin.hydration],
    ];
    dims.forEach(([label, val]) => {
      y = ensureSpace(doc, y, 12);
      const key = label.toLowerCase();
      const ins = twinLookup[key];
      const status = ins?.status ? ` — ${ins.status}` : "";
      y = bodyText(doc, `• ${label}: ${val ?? "—"}/100${status}`, y, { bold: true });
      if (ins?.note) y = bodyText(doc, `  ${ins.note}`, y);
    });
    y += 3;
  }

  // Factors
  if (assessment.factors?.length) {
    y = ensureSpace(doc, y, 20);
    y = sectionTitle(doc, "How your score was calculated", y);
    assessment.factors.forEach((f) => {
      y = ensureSpace(doc, y, 15);
      y = bodyText(doc, `• ${f.name} (${f.impact})`, y, { bold: true });
      if (f.value) y = bodyText(doc, `  You said: ${f.value}`, y);
      if (f.detail) y = bodyText(doc, `  ${f.detail}`, y);
      y += 1;
    });
  }

  // Recommendations
  if (assessment.recommendations?.length) {
    y = ensureSpace(doc, y, 20);
    y = sectionTitle(doc, "Evidence-based recommendations", y);
    assessment.recommendations.forEach((r, i) => {
      y = ensureSpace(doc, y, 20);
      const title = typeof r === "string" ? r : r?.title;
      y = bodyText(doc, `${i + 1}. ${title}`, y, { bold: true });
      if (r?.why) y = bodyText(doc, `Why: ${r.why}`, y);
      if (r?.benefit) y = bodyText(doc, `Benefit: ${r.benefit}`, y);
      if (r?.consult_note)
        y = bodyText(doc, `Note: ${r.consult_note}`, y, {
          color: [120, 80, 0],
        });
      y += 1;
    });
  }

  // Problems
  if (assessment.problems?.length) {
    y = ensureSpace(doc, y, 20);
    y = sectionTitle(doc, "Potential areas to watch", y);
    assessment.problems.forEach((p, i) => {
      y = ensureSpace(doc, y, 18);
      y = bodyText(doc, `${i + 1}. ${p.problem}`, y, { bold: true });
      if (p.cause) y = bodyText(doc, `Cause: ${p.cause}`, y);
      if (p.solution) y = bodyText(doc, `Approach: ${p.solution}`, y);
      y += 1;
    });
  }

  // Diet plan
  if (assessment.diet_plan?.length) {
    y = ensureSpace(doc, y, 20);
    y = sectionTitle(doc, "7-Day Personalized Indian Diet Plan", y);
    if (assessment.hydration_recommendation) {
      y = bodyText(
        doc,
        `Water: ${assessment.hydration_recommendation}`,
        y,
        { color: [11, 61, 145] }
      );
      y += 1;
    }
    assessment.diet_plan.forEach((d) => {
      y = ensureSpace(doc, y, 26);
      y = bodyText(doc, d.day, y, { bold: true, size: 11 });
      if (d.reason)
        y = bodyText(doc, `Why: ${d.reason}`, y, { color: [90, 90, 90] });
      y = bodyText(doc, `Breakfast: ${d.breakfast}`, y);
      y = bodyText(doc, `Lunch: ${d.lunch}`, y);
      y = bodyText(doc, `Dinner: ${d.dinner}`, y);
      y = bodyText(doc, `Snack: ${d.snack}`, y);
      y += 1;
    });
  }

  // Disclaimer
  y = ensureSpace(doc, y, 24);
  line(doc, y);
  y += 4;
  y = bodyText(
    doc,
    "Medical Disclaimer: SilentScan AI is an AI-powered health awareness tool. It does not diagnose diseases or replace qualified healthcare professionals. Always consult a doctor for medical decisions.",
    y,
    { color: [120, 80, 0] }
  );

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `SilentScan AI · nutriplate07.com · Page ${i} / ${pageCount}`,
      14,
      doc.internal.pageSize.getHeight() - 8
    );
  }

  doc.save(`silentscan-health-report-${Date.now()}.pdf`);
};

export const downloadMentalReportPDF = (assessment) => {
  if (!assessment) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const today = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  drawHeader(doc, `Mental Wellness Report — ${today}`);
  let y = 32;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(11, 61, 145);
  doc.text(`${assessment.score}/100`, 14, y);
  doc.setFontSize(12);
  doc.setTextColor(80, 80, 80);
  doc.text(assessment.label || "", 45, y);
  y += 8;
  doc.setDrawColor(0, 229, 255);
  doc.setLineWidth(0.8);
  doc.line(14, y, 80, y);
  y += 6;
  doc.setLineWidth(0.1);

  y = bodyText(doc, assessment.summary || "", y);
  if (assessment.confidence)
    y = bodyText(doc, `Confidence: ${assessment.confidence}`, y, {
      color: [90, 90, 90],
    });
  y += 2;
  line(doc, y);
  y += 6;

  y = sectionTitle(doc, "Wellness dimensions", y);
  const dims = [
    ["Stress", assessment.stress_score, assessment.stress_level],
    ["Sleep", assessment.sleep_score, assessment.sleep_quality],
    ["Emotional", assessment.emotional_score, assessment.emotional_wellbeing],
    ["Lifestyle", assessment.lifestyle_score, assessment.lifestyle_balance],
  ];
  dims.forEach(([k, v, tag]) => {
    y = bodyText(doc, `${k}: ${v}/100  (${tag || "—"})`, y);
  });
  y += 3;

  if (assessment.positive_habits?.length) {
    y = ensureSpace(doc, y, 16);
    y = sectionTitle(doc, "Positive habits", y);
    assessment.positive_habits.forEach((h) => {
      y = bodyText(doc, `• ${h}`, y);
    });
    y += 1;
  }
  if (assessment.improvement_areas?.length) {
    y = ensureSpace(doc, y, 16);
    y = sectionTitle(doc, "Areas to improve", y);
    assessment.improvement_areas.forEach((h) => {
      y = bodyText(doc, `• ${h}`, y);
    });
    y += 1;
  }
  if (assessment.factors?.length) {
    y = ensureSpace(doc, y, 20);
    y = sectionTitle(doc, "Why you received this score", y);
    assessment.factors.forEach((f) => {
      y = ensureSpace(doc, y, 12);
      y = bodyText(doc, `• ${f.name} (${f.impact})`, y, { bold: true });
      if (f.detail) y = bodyText(doc, `  ${f.detail}`, y);
      y += 1;
    });
  }
  if (assessment.recommendations?.length) {
    y = ensureSpace(doc, y, 20);
    y = sectionTitle(doc, "Personalized suggestions", y);
    assessment.recommendations.forEach((r, i) => {
      y = ensureSpace(doc, y, 15);
      y = bodyText(doc, `${i + 1}. ${r.title}`, y, { bold: true });
      if (r.why) y = bodyText(doc, `Why: ${r.why}`, y);
      if (r.benefit) y = bodyText(doc, `Benefit: ${r.benefit}`, y);
      y += 1;
    });
  }

  y = ensureSpace(doc, y, 24);
  line(doc, y);
  y += 4;
  y = bodyText(
    doc,
    "Disclaimer: SilentScan AI provides mental wellness awareness only. It does not diagnose mental health conditions. Please consult qualified mental-health professionals for diagnosis or treatment.",
    y,
    { color: [120, 80, 0] }
  );

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `SilentScan AI · nutriplate07.com · Page ${i} / ${pageCount}`,
      14,
      doc.internal.pageSize.getHeight() - 8
    );
  }

  doc.save(`silentscan-mental-report-${Date.now()}.pdf`);
};

// ---- Diabetes Centre: summary prepared for a doctor's visit ----
export const downloadDoctorSummaryPDF = ({ report, readings = [], plan }) => {
  const doc = new jsPDF();
  drawHeader(doc, "AI-generated support summary — prepared for discussion with your healthcare professional.");

  let y = 34;
  y = bodyText(
    doc,
    "This summary organizes the patient's self-recorded monitoring data and AI-generated supportive lifestyle suggestions. It is NOT a diagnosis and has NOT been reviewed or approved by a doctor.",
    y,
    { size: 9, color: [120, 60, 0] }
  );
  y += 2;
  line(doc, y);
  y += 8;

  y = sectionTitle(doc, "Doctor-documented information", y);
  y = bodyText(doc, `Condition as written: ${report?.condition_as_written || "Not provided"}`, y);
  if (report?.instructions?.length) y = bodyText(doc, `Instructions: ${report.instructions.join("; ")}`, y);
  if (report?.avoid_foods?.length) y = bodyText(doc, `Foods to avoid (documented): ${report.avoid_foods.join(", ")}`, y);
  if (report?.activity_restrictions?.length) y = bodyText(doc, `Activity restrictions: ${report.activity_restrictions.join("; ")}`, y);
  if (report?.follow_up) y = bodyText(doc, `Follow-up: ${report.follow_up}`, y);
  y = ensureSpace(doc, y, 10);
  line(doc, y);
  y += 8;

  y = sectionTitle(doc, "Recent glucose readings (with measurement context)", y);
  if (!readings.length) {
    y = bodyText(doc, "No readings recorded.", y);
  } else {
    readings.slice(0, 12).forEach((r) => {
      y = ensureSpace(doc, y, 8);
      y = bodyText(
        doc,
        `${r.date} ${r.time || ""} — ${r.value} ${r.unit} (${r.context_label || r.context})${r.flag === "low" ? "  [below 60 mg/dL]" : ""}${r.flag === "high" ? "  [above 180 mg/dL]" : ""}`,
        y,
        { size: 9 }
      );
    });
    const vals = readings.map((r) => r.value_mgdl).filter((v) => typeof v === "number");
    if (vals.length) {
      const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
      y = bodyText(
        doc,
        `Trend summary: ${vals.length} readings, average ≈ ${avg} mg/dL, range ${Math.round(Math.min(...vals))}–${Math.round(Math.max(...vals))} mg/dL. For monitoring discussion only — not a diagnosis.`,
        y,
        { size: 9 }
      );
    }
  }
  y = ensureSpace(doc, y, 10);
  line(doc, y);
  y += 8;

  if (plan) {
    y = sectionTitle(doc, `AI-generated supportive guidance (${plan.label || "latest plan"})`, y);
    if (plan.diet_support?.length) y = bodyText(doc, `Diet support: ${plan.diet_support.join("; ")}`, y, { size: 9 });
    if (plan.lifestyle_support?.length) y = bodyText(doc, `Lifestyle support: ${plan.lifestyle_support.join("; ")}`, y, { size: 9 });
    if (plan.why_changed) y = bodyText(doc, `Why this version: ${plan.why_changed}`, y, { size: 9 });
    y = ensureSpace(doc, y, 10);
    line(doc, y);
    y += 8;
  }

  y = sectionTitle(doc, "Questions to discuss with the doctor", y);
  const qs = plan?.questions_for_doctor?.length
    ? plan.questions_for_doctor
    : ["Are my current readings within the range you expect for me?", "Is my current monitoring frequency still right for me?"];
  qs.forEach((q) => {
    y = ensureSpace(doc, y, 8);
    y = bodyText(doc, `• ${q}`, y, { size: 9 });
  });

  y = ensureSpace(doc, y, 16);
  line(doc, y);
  y += 6;
  bodyText(
    doc,
    "SilentScan AI is an educational wellness simulator. This document does not provide medical evaluation, clinical testing, or diagnosis, and it does not replace professional medical advice.",
    y,
    { size: 8, color: [110, 110, 110] }
  );

  doc.save("doctor-visit-summary.pdf");
};
